/**
 * Manage Tracking Policy
 *
 * Allow a user to allow or deny tracking scripts.  The selection is persisted
 * using a cookie (irony).
 *
 * How To Use:
 *
 * 1. Mark scripts that should only be run if tracking cookies have been allowed
 * with `type=text/plain` and `data-type=text/javascript` attributes.  These will
 * be run automatically once the policy is set to 'allow'.
 *
 * 2. Call `cookieConsent.getCookiePolicy()` on page load to determine if
 * the user's consent needs to be obtained.  You can also check if the cookie is
 * set server-side.
 *
 * 3. If the user consents, call `cookieConsent.setCookiePolicy(true)`.  If the
 * user does not consent, call `cookieConset.setCookiePolicy(false).
 *
 * 4. Call `cookieConsent.applyCookiePolicy()` to load scripts if allowed
 *
 * 5. Call `cookieConsent.clearCookiePolicy()` to remove the existing selection
 * and reload the page (which should prompt user to make a new selection)
 */
(function () {
  const COOKIE_NAME = 'allowTrackingCookies'
  const COOKIE_EXPIRY = 365 // days

  /**
   * Set a cookie
   * @private
   * @param {string} name - Name of cookie
   * @param {string} value - Value of cookie
   * @param {number} days - Days until expiry
   */
  const setCookie = (name, value, days) => {
    const date = new Date()
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000))
    const expiry = date.toUTCString()
    document.cookie = `${name}=${value}; expires=${expiry}; path=/`
  }

  /**
   * Get a cookie
   * @private
   * @param {string} name - Name of cookie
   * @returns Value of cookie, or undefined if cookie was not found
   */
  const getCookie = (name) => {
    const cookies = document.cookie.split('; ')
    const cookie = cookies.find(cookie => cookie.startsWith(COOKIE_NAME))
    if (cookie) return cookie.split('=')[1]
  }

  /**
   * This finds all "conditional" scripts, turns them into proper scripts and then
   * executes them.
   */
  const runScripts = () => {
    const scripts = document.querySelectorAll('script[type="text/plain"][data-type="text/javascript"]')
    scripts.forEach(script => {
      script.type = script.getAttribute('data-type')
      eval(script.innerHTML) // eslint-disable-line no-eval
    })
  }

  /**
   * Get current cookie policy setting
   * @returns {string} allow, deny, or prompt
   */
  const getCookiePolicy = () => {
    return getCookie(COOKIE_NAME) || 'prompt'
  }

  /**
   * Persist user selection
   * @param {boolean} allowed - true if tracking scripts are allowed
   * @param {Object} event - Event.  Use if invoking script from an anchor.
   */
  const setCookiePolicy = (allowed, event) => {
    if (event) event.preventDefault()
    if (allowed) {
      setCookie(COOKIE_NAME, 'allow', COOKIE_EXPIRY)
    } else {
      setCookie(COOKIE_NAME, 'deny', COOKIE_EXPIRY)
    }
  }

  /**
   * Apply the selected cookie policy
   */
  const applyCookiePolicy = () => {
    switch (getCookiePolicy()) {
      case 'allow':
        runScripts()
        break
      case 'deny':
        break
      default:
        // the default policy should match the default checkbox state
        // in this case, allowed by default
        setCookiePolicy(true)
        runScripts()
    }
  }

  /**
   * Sets the cookie policy based on the state of a checkbox.  'allow' when checked,
   * 'deny' when unchecked.
   * @param {*} element - A checkbox element.
   */
  const checkboxTogglePolicy = (checkbox) => {
    setCookiePolicy(checkbox.checked)
  }

  /**
   * Remove user selection and reload the page (to redisplay prompt)
   * @param {Event} event - use if invoking script from an achor.
   */
  const clearCookiePolicy = (event) => {
    if (event) event.preventDefault()
    setCookie(COOKIE_NAME, 'prompt', -1)
    window.location.reload()
  }

  /**
   * Check for previously applied policy, and if set, run scripts.
   */
  document.addEventListener('DOMContentLoaded', () => {
    // if user has previously allowed cookies, run scripts now
    if (getCookie(COOKIE_NAME) === 'allow') runScripts()
  })

  /**
   * Expose public methods
   */
  window.cookieConsent = {
    getCookiePolicy,
    setCookiePolicy,
    applyCookiePolicy,
    clearCookiePolicy,
    checkboxTogglePolicy
  }
})()
